import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useProducts } from '@/contexts/productContext.tsx'
import { modParamsFromURL, modParamsToAPI, useBasket } from '@/contexts/basketContext.tsx'
import { Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack } from '@tabler/icons-react'
import { useState, useEffect } from 'react'
import { AlbumFile } from '@/models/albumFile.ts'
import { CropData, LocationState } from '@/types/photoCrop.ts'
import { PhotoCropHeader } from '@/components/shop/PhotoCropHeader.tsx'
import { PhotoCropList } from '@/components/shop/PhotoCropList.tsx'
import { PhotoCropBottomActions } from '@/components/shop/PhotoCropBottomActions.tsx'
import { CropConfirmModal, useCropConfirmModal } from '@/components/shop/CropConfirmModal.tsx'

export default function ProductPhotoCropPage() {
  const { product_sku, sku_suffix, driveId } = useParams<{ product_sku: string; sku_suffix: string; driveId: string }>()
  const { products, isLoading } = useProducts()
  const { addItem, refetch: refetchBasket } = useBasket()
  const navigate = useNavigate()
  const location = useLocation()

  // Get mod parameters from URL
  // Get data from navigation state
  const locationState = location.state as LocationState
  const navigationSelectedImages = locationState?.selectedImages || []
  const navigationCropData = locationState?.cropData || {}

  // Storage key for persisting selection
  const storageKey = `product-selection-${product_sku}-${sku_suffix}`

  // Try to get from sessionStorage if no navigation state
  const getStoredSelection = (): AlbumFile[] => {
    try {
      const stored = sessionStorage.getItem(storageKey)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  const initialSelectedImages = navigationSelectedImages.length > 0
    ? navigationSelectedImages
    : getStoredSelection()
  const initialCropData = navigationCropData

  // Find product by SKU
  const product = products.find(p => p.sku === product_sku)
  const variation = product?.variations.find(v => v.sku_suffix === sku_suffix)

  // State
  const [selectedImages, setSelectedImages] = useState<AlbumFile[]>(initialSelectedImages)
  const [cropData, setCropData] = useState<CropData>(initialCropData)
  const [isProcessing, setIsProcessing] = useState(false)
  const { modalRef, show: showConfirmModal } = useCropConfirmModal()

  // Get aspect ratio from variation (assuming it has width/height properties)
  const getAspectRatio = (rotated: boolean = false) => {
    const defaultWidth = variation!.property_value_links.find(prop => prop.property_value.width_mm != null)!.property_value.width_mm!
    const defaultHeight = variation!.property_value_links.find(prop => prop.property_value.height_mm != null)!.property_value.height_mm!

    return rotated ? defaultHeight / defaultWidth : defaultWidth / defaultHeight
  }

  // Check if rotation is disabled for any property value or if dimensions are square
  const isRotationDisabled = variation?.property_value_links.some(
    prop => prop.property_value.disable_rotation === true ||
            (prop.property_value.width_mm != null &&
             prop.property_value.height_mm != null &&
             prop.property_value.width_mm === prop.property_value.height_mm)
  ) ?? false

  // Initialize crop data for images that don't have it
  useEffect(() => {
    const newCropData = { ...cropData }
    let hasChanges = false

    selectedImages.forEach(image => {
      if (!variation) {
        return
      }

      if (!newCropData[image.id]) {
        // Determine optimal rotation based on image dimensions and aspect ratio
        const imageAspectRatio = (image.width ?? 1) / (image.height ?? 1)
        const targetAspectRatio = getAspectRatio(false) // Normal aspect ratio
        const targetAspectRatioRotated = getAspectRatio(true) // Rotated aspect ratio

        // Calculate how much of the image surface area we can use with each orientation
        let normalCoverage, rotatedCoverage

        if (imageAspectRatio > targetAspectRatio) {
          // Image is wider than target - limited by height
          normalCoverage = targetAspectRatio / imageAspectRatio
        } else {
          // Image is taller than target - limited by width
          normalCoverage = imageAspectRatio / targetAspectRatio
        }

        if (imageAspectRatio > targetAspectRatioRotated) {
          // Image is wider than rotated target - limited by height
          rotatedCoverage = targetAspectRatioRotated / imageAspectRatio
        } else {
          // Image is taller than rotated target - limited by width
          rotatedCoverage = imageAspectRatio / targetAspectRatioRotated
        }

        // Choose the orientation that maximizes surface area coverage
        const shouldRotate = rotatedCoverage > normalCoverage

        newCropData[image.id] = {
          crop: {
            x: 25,
            y: 25,
            width: 50,
            height: 50
          },
          count: 1,
          rotated: shouldRotate
        }
        hasChanges = true
      }
    })

    if (hasChanges) {
      setCropData(newCropData)
    }
  }, [variation, selectedImages])

  const updateCrop = (imageId: string, crop: { x: number; y: number; width: number; height: number }) => {
    setCropData(prev => ({
      ...prev,
      [imageId]: {
        ...prev[imageId],
        crop // Store the percentage-based crop directly
      }
    }))
  }

  const updateCount = (imageId: string, delta: number) => {
    setCropData(prev => ({
      ...prev,
      [imageId]: {
        ...prev[imageId],
        count: Math.max(1, prev[imageId].count + delta)
      }
    }))
  }

  const toggleRotation = (imageId: string) => {
    setCropData(prev => ({
      ...prev,
      [imageId]: {
        ...prev[imageId],
        rotated: !prev[imageId].rotated
      }
    }))
  }

  const removeImage = (imageId: string) => {
    setSelectedImages(prev => prev.filter(img => img.id !== imageId))
    setCropData(prev => {
      const newData = { ...prev }
      delete newData[imageId]
      return newData
    })
  }

  const goBackToSelection = () => {
    // Store current state in sessionStorage before navigating
    const storageKey = `product-selection-${product_sku}-${sku_suffix}`
    sessionStorage.setItem(storageKey, JSON.stringify(selectedImages))

    // Preserve mod parameters when navigating back
    const modParamsString = modParamsToAPI(modParamsFromURL(), product!).toString()
    navigate(`/d/${driveId}/shop/product/${product_sku}/${sku_suffix}/select${modParamsString ? `?${modParamsString}` : ''}`, {
      state: {
        selectedImages,
        cropData
      }
    })
  }

  const handleContinueToBasket = async () => {
    if (!product || !variation) return

    setIsProcessing(true)

    try {
      const fileIds = selectedImages.map(img => img.id)

      // Convert cropData to the format expected by the API
      const cropDataForAPI: Record<string, any> = {}
      selectedImages.forEach(image => {
        const imageCropData = cropData[image.id]
        if (imageCropData) {
          cropDataForAPI[image.id] = {
            crop_x: imageCropData.crop.x,           // Percentage (0-100)
            crop_y: imageCropData.crop.y,           // Percentage (0-100)
            crop_width: imageCropData.crop.width,   // Percentage (0-100)
            crop_height: imageCropData.crop.height, // Percentage (0-100)
            crop_rotated: imageCropData.rotated,
            count: imageCropData.count
          }
        }
      })

      // Debug log to verify crop values are percentages (0-100)
      console.log('Crop data being sent to API:', cropDataForAPI)

      // Build mods object from URL parameters
      const modsForAPI = modParamsToAPI(modParamsFromURL(), product)

      // Use the basket context method to add to basket
      await addItem({
        product: product.id,
        variation: variation.id,
        quantity: 1,
        files: fileIds,
        crop_data: cropDataForAPI,
        mods: modsForAPI,
        drive_id: driveId!,
      })

      // Clean up sessionStorage after successful basket addition
      const storageKey = `product-selection-${product_sku}-${sku_suffix}`
      sessionStorage.removeItem(storageKey)

      // Refresh basket to ensure it's up to date, before navigating to prevent flashing
      await refetchBasket()

      navigate(`/d/${driveId}/shop/basket`)
    } catch (error) {
      console.error('Error adding to basket:', error)
      alert('Fehler beim Hinzufügen zum Warenkorb')
    } finally {
      setIsProcessing(false)
    }
  }

  const getTotalImageCount = () => {
    return Object.values(cropData).reduce((total, data) => total + data.count, 0)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (!product || !variation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">Produkt oder Variante nicht gefunden</h1>
          <Button color="primary" variant="outline" onClick={() => navigate(`/d/${driveId}/shop`)}>
            <IconArrowBack size={20} />
            Zurück zum Shop
          </Button>
        </div>
      </div>
    )
  }

  if (selectedImages.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">Keine Bilder ausgewählt</h1>
          <Button
            color="primary"
            variant="outline"
            onClick={() => {
              const modParamsString = modParamsFromURL().toString()
              navigate(`/d/${driveId}/shop/product/${product_sku}/${sku_suffix}/select${modParamsString ? `?${modParamsString}` : ''}`)
            }}
          >
            <IconArrowBack size={20} />
            Zurück zur Bildauswahl
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <PhotoCropHeader
        title={product.title}
        subtitle={variation.full_title}
        selectedImagesCount={selectedImages.length}
        totalPrintCount={getTotalImageCount()}
      />

      <PhotoCropList
        selectedImages={selectedImages}
        cropData={cropData}
        getAspectRatio={getAspectRatio}
        updateCrop={updateCrop}
        updateCount={updateCount}
        toggleRotation={toggleRotation}
        removeImage={removeImage}
        disableRotation={isRotationDisabled}
      />

      <CropConfirmModal
        modalRef={modalRef}
        onConfirm={handleContinueToBasket}
        isLoading={isProcessing}
      />

      <PhotoCropBottomActions
        onBack={goBackToSelection}
        onContinue={showConfirmModal}
        isProcessing={isProcessing}
        disabled={isProcessing || selectedImages.length === 0}
        continueText="In den Warenkorb"
        totalCount={getTotalImageCount()}
      />
    </div>
  )
}

